import React, { ReactNode, useState } from 'react';
import BaseErrorBoundary from './BaseErrorBoundary';

interface GlobalErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: any) => void;
}

const GlobalErrorBoundary: React.FC<GlobalErrorBoundaryProps> = ({ 
  children, 
  fallback,
  onError 
}) => {
  const [currentError, setCurrentError] = useState<Error | null>(null);
  const handleGlobalError = (error: Error, errorInfo: any) => {
    // Store the current error in state so it can be accessed in JSX
    setCurrentError(error);
    
    // Additional global error handling logic
    console.error('🚨 GLOBAL ERROR BOUNDARY TRIGGERED:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    });

    // Send critical error to monitoring service
    // Example: Send to Sentry, LogRocket, or other monitoring tools
    try {
      // Uncomment and configure when you have error monitoring setup
      // window.Sentry?.captureException(error, {
      //   level: 'error',
      //   tags: {
      //     errorBoundary: 'global',
      //     location: window.location.pathname
      //   },
      //   extra: {
      //     componentStack: errorInfo.componentStack,
      //     timestamp: new Date().toISOString()
      //   }
      // });

      // Or send to your own error logging endpoint
      // fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     message: error.message,
      //     stack: error.stack,
      //     componentStack: errorInfo.componentStack,
      //     url: window.location.href,
      //     timestamp: new Date().toISOString(),
      //     userAgent: navigator.userAgent
      //   })
      // });
    } catch (reportingError) {
      console.error('Failed to report error to monitoring service:', reportingError);
    }

    // Call custom error handler if provided
    onError?.(error, errorInfo);
  };

  const globalFallback = fallback || (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="mb-4">
              <i className="ri-bug-line text-danger" style={{ fontSize: '5rem' }}></i>
            </div>
            <h1 className="display-6 mb-3">Oops! Something went wrong</h1>
            <p className="lead text-muted mb-4">
              We're sorry, but the application has encountered an unexpected error. 
              Our team has been notified and is working to fix this issue.
            </p>
            
            <div className="d-flex gap-3 justify-content-center mb-4">
              <button 
                className="btn btn-primary btn-lg"
                onClick={() => window.location.reload()}
              >
                <i className="ri-refresh-line me-2"></i>
                Reload Application
              </button>
              <button 
                className="btn btn-outline-secondary btn-lg"
                onClick={() => window.history.back()}
              >
                <i className="ri-arrow-left-line me-2"></i>
                Go Back
              </button>
            </div>

            <div className="text-muted">
              <small>
                Error ID: {Date.now().toString(36).toUpperCase()}
                <br />
                Time: {new Date().toLocaleString()}
              </small>
            </div>

            {/* Help section */}
            <div className="mt-5 pt-4 border-top">
              <h6 className="text-muted mb-3">Need immediate help?</h6>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <button 
                  className="btn btn-link btn-sm"
                  onClick={() => window.location.href = '/'}
                >
                  <i className="ri-home-line me-1"></i>
                  Go to Homepage
                </button>
                <button 
                  className="btn btn-link btn-sm"
                  onClick={() => window.location.href = 'mailto:support@yourcompany.com'}
                >
                  <i className="ri-mail-line me-1"></i>
                  Contact Support
                </button>
                <button 
                  className="btn btn-link btn-sm"
                  onClick={() => {
                    const errorDetails = `Error: ${currentError?.message || 'Unknown error'}\nTime: ${new Date().toISOString()}\nURL: ${window.location.href}`;
                    navigator.clipboard?.writeText(errorDetails);
                    alert('Error details copied to clipboard');
                  }}
                >
                  <i className="ri-clipboard-line me-1"></i>
                  Copy Error Details
                </button>
              </div>
            </div>

            {/* Development mode error details */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mt-4">
                <details className="text-start">
                  <summary className="btn btn-outline-info btn-sm">
                    <i className="ri-bug-line me-1"></i>
                    Show Technical Details (Development Mode)
                  </summary>
                  <div className="mt-3 p-3 bg-dark text-light rounded">
                    <h6 className="text-warning">Error Information:</h6>
                    <pre className="small mb-3">
                      <code>{JSON.stringify({
                        message: currentError?.message || 'Unknown error',
                        stack: currentError?.stack || 'No stack trace available',
                        url: window.location.href,
                        timestamp: new Date().toISOString()
                      }, null, 2)}</code>
                    </pre>
                  </div>
                </details>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <BaseErrorBoundary
      level="global"
      moduleName="Application"
      fallback={globalFallback}
      onError={handleGlobalError}
    >
      {children}
    </BaseErrorBoundary>
  );
};

export default GlobalErrorBoundary;
